import type { SavedView } from "../domain/app-types.ts";
import { activeTags } from "../features/tasks/selectors.ts";
import { blockingReviewTasks } from "../features/tasks/weekly-review-action-controller.ts";
import { addDays } from "../features/tasks/recurrence.ts";
import { createSavedViewRuntimeController } from "../features/navigation/saved-view-runtime-controller.ts";
import { createSidebarInteractionController } from "../features/navigation/sidebar-interaction-controller.ts";
import { createSidebarRuntime } from "../features/navigation/sidebar-runtime.ts";
import type { NavigationState } from "../features/navigation/state.ts";
import type { TaskCollectionState } from "../features/tasks/task-collection-state.ts";
import type { TaskViewState } from "../features/tasks/task-view-state.ts";
import type { WorkspaceAccessState } from "../features/collaboration/workspace-access-state.ts";
import { setMeta } from "../infrastructure/storage/indexed-db.ts";
import { today } from "../ui/date.ts";
import type { SessionState } from "./session-state.ts";
import type { ShellDataState } from "./shell-data-state.ts";

export interface NavigationCompositionDependencies {
  document: Document;
  taskView: TaskViewState;
  taskCollectionState: TaskCollectionState;
  sessionState: SessionState;
  workspaceAccess: WorkspaceAccessState;
  shellDataState: ShellDataState;
  navigationState: NavigationState;
  changePublished(id: string, rootId: string, update: (view: SavedView) => void): Promise<void>;
  sync(): void;
  icon(name: string): string;
}

export function createNavigationComposition(deps: NavigationCompositionDependencies) {
  const { taskView, taskCollectionState, sessionState, workspaceAccess, shellDataState, navigationState } = deps;
  const savedViewRuntime = createSavedViewRuntimeController({
    views: () => taskView.savedViews,
    updatePersonalViews: () => taskView.updatePersonalSavedViews(),
    activateState: view => taskView.activateSavedView(view),
    setPageSize: value => { taskCollectionState.pageSize = value; },
    resetPage: () => { taskCollectionState.resetPage(); },
    clearCategory: () => { sessionState.activeCollectionId = null; },
    persistPersonalViews: views => setMeta("savedViews", views),
    changePublished: deps.changePublished,
    markLocal: () => { sessionState.markLocal(); },
    sync: deps.sync,
    filterContext: { today, addDays, now: Date.now, tags: activeTags, sharedTaskIds: () => workspaceAccess.outgoingSharedTaskIds, tasks: () => shellDataState.todos, blockers: blockingReviewTasks },
  });
  const sidebarInteractionController = createSidebarInteractionController({
    document: deps.document,
    activeView: () => taskView.view,
    activeSavedView: () => taskView.activeSavedView,
    expandedGroup: () => navigationState.expandedGroup,
    groupSelectionMade: () => navigationState.groupSelectionMade,
    expandedSpace: () => navigationState.expandedSpaceId,
    openMenu: () => navigationState.openMenu,
    setOpenMenu: value => { navigationState.openMenu = value; },
  });
  const sidebarRuntime = createSidebarRuntime({
    navigation: navigationState, roots: () => sessionState.roots, activeRootId: () => sessionState.activeRootId, activeCollectionId: () => sessionState.activeCollectionId,
    collectionRoles: () => workspaceAccess.collectionRoles, savedViews: () => taskView.savedViews, persistRecent: destinations => setMeta("recentDestinations", destinations),
    currentGroup: sidebarInteractionController.currentGroup, updateGroupDisclosures: sidebarInteractionController.updateGroupDisclosures,
    updateSpaceDisclosures: sidebarInteractionController.updateSpaceDisclosures, closeMenu: sidebarInteractionController.closeMenu, icon: deps.icon,
  });
  return { savedViewRuntime, sidebarRuntime, sidebarInteractionController };
}
